package de.xtl.retroguide

import de.xtl.retroguide.protocol.MAX_ROTI
import de.xtl.retroguide.protocol.ParticipantRole
import de.xtl.retroguide.protocol.QuestionSummary
import de.xtl.retroguide.protocol.QuestionWidget
import de.xtl.retroguide.protocol.toActionItem
import de.xtl.retroguide.protocol.RadarDimension
import de.xtl.retroguide.protocol.SessionSnapshot
import de.xtl.retroguide.protocol.VisibleAnswer
import de.xtl.retroguide.protocol.categories
import de.xtl.retroguide.protocol.toCategorizedAnswer

internal val SessionSnapshot.markdownFileName: String
    get() = "retro-${sessionCode.lowercase()}.md"

internal fun SessionSnapshot.toMarkdown(): String = buildString {
    appendLine("# ${title.markdownText()}")
    appendLine()
    appendLine("Session-Code: `$sessionCode`")
    appendLine()
    appendLine("## Beteiligte")
    participants.firstOrNull { it.role == ParticipantRole.MODERATOR }?.let {
        appendLine("- Moderation: ${it.displayName.markdownText()}")
    }
    participants.filter { it.role == ParticipantRole.PARTICIPANT }.forEach {
        appendLine("- ${it.displayName.markdownText()}")
    }
    val actionItems = questionSummaries
        .filter { it.question.widget == QuestionWidget.ACTION_ITEMS }
        .flatMap { summary -> summary.answers.mapNotNull { it.content.toActionItem() } }
    if (actionItems.isNotEmpty()) {
        appendLine()
        appendLine("## Maßnahmen")
        appendLine()
        actionItems.forEach { appendLine("- [ ] ${it.summary.markdownText()}") }
    }
    questionSummaries.forEachIndexed { index, summary ->
        appendLine()
        appendLine("## ${index + 1}. ${summary.question.text.markdownText()}")
        appendLine()
        if (summary.answers.isEmpty()) {
            appendLine("_Keine Antworten_")
        } else {
            val widget = summary.question.widget
            when {
                widget.categories.isNotEmpty() -> appendCategorizedAnswers(summary.answers, widget)
                widget == QuestionWidget.ROTI -> appendRotiAnswers(summary.answers)
                widget == QuestionWidget.TEAM_RADAR -> appendRadarAnswers(summary.answers)
                widget == QuestionWidget.DOT_VOTING -> appendVotingRanking(summary)
                widget == QuestionWidget.ACTION_ITEMS -> summary.answers.forEach { answer ->
                    val text = answer.content.toActionItem()?.summary ?: answer.content
                    appendLine("- **${answer.participantName.markdownText()}:** ${text.markdownText()}")
                }
                else -> summary.answers.forEach { answer ->
                    appendLine("- **${answer.participantName.markdownText()}:** ${answer.content.markdownText()}")
                }
            }
            appendClusters(summary)
        }
    }
}.trimEnd() + "\n"

private fun StringBuilder.appendCategorizedAnswers(answers: List<VisibleAnswer>, widget: QuestionWidget) {
    widget.categories.forEach { category ->
        val entries = answers.mapNotNull { answer ->
            answer.content.toCategorizedAnswer()
                ?.takeIf { it.category == category }
                ?.let { answer.participantName to it.text }
        }
        if (entries.isEmpty()) return@forEach
        appendLine("### ${category.emoji} ${category.exportLabel}")
        appendLine()
        entries.forEach { (name, text) -> appendLine("- **${name.markdownText()}:** ${text.markdownText()}") }
        appendLine()
    }
}

private fun StringBuilder.appendRotiAnswers(answers: List<VisibleAnswer>) {
    answers.rotiStats()?.let { stats ->
        val votes = if (stats.votes == 1) "1 Stimme" else "${stats.votes} Stimmen"
        appendLine("Durchschnitt: ${stats.average.formatOneDecimal()} von $MAX_ROTI ($votes)")
        appendLine()
    }
    answers.forEach { answer ->
        appendLine("- **${answer.participantName.markdownText()}:** ${answer.content.markdownText()}")
    }
}

private fun StringBuilder.appendClusters(summary: QuestionSummary) {
    if (summary.clusters.isEmpty()) return
    val answersById = summary.answers.associateBy { it.id }
    appendLine()
    appendLine("#### Themen")
    appendLine()
    summary.clusters.forEach { cluster ->
        val texts = cluster.answerIds.mapNotNull { answersById[it]?.plainText?.markdownText() }
        appendLine("- **${cluster.name.markdownText()}** (${cluster.answerIds.size}): ${texts.joinToString(" · ")}")
    }
}

private fun StringBuilder.appendVotingRanking(summary: QuestionSummary) {
    votingRanking(summary.votingOptions, summary.answers).forEachIndexed { index, ranked ->
        val points = if (ranked.points == 1) "1 Punkt" else "${ranked.points} Punkte"
        appendLine("${index + 1}. ${ranked.option.label.markdownText()} – $points")
    }
}

private fun StringBuilder.appendRadarAnswers(answers: List<VisibleAnswer>) {
    val stats = answers.radarStats() ?: run {
        appendLine("_Keine vollständigen Antworten_")
        return
    }
    val names = stats.answers.map { (name, _) -> name.markdownText().replace("|", "\\|") }
    appendLine("| Dimension | Ø | ${names.joinToString(" | ")} |")
    appendLine("|---|---|${names.joinToString("") { "---|" }}")
    RadarDimension.entries.forEach { dimension ->
        val scores = stats.answers.joinToString(" | ") { (_, radar) -> radar.scores.getValue(dimension).toString() }
        appendLine("| ${dimension.exportLabel} | ${stats.averages.getValue(dimension).formatOneDecimal()} | $scores |")
    }
    appendLine()
    appendLine(if (stats.answers.size == 1) "1 Antwort" else "${stats.answers.size} Antworten")
}

private fun String.markdownText() = replace("\r\n", "\n")
    .replace('\r', '\n')
    .replace("\\", "\\\\")
    .replace("*", "\\*")
    .replace("_", "\\_")
    .replace("`", "\\`")
    .replace("[", "\\[")
    .replace("]", "\\]")
    .replace("\n", "\n  ")
